import { Component } from 'react'
import { IClientUID } from "../../../api/clients"
import { toast } from 'react-toastify'
import { GetFilterFillerSettings, SetFilterFillerSettings, SettingsData } from '../../../api/0x0004/filter-filler'
import { ErrorAlert } from './components/ErrorAlert'
import classNames from 'classnames'

type Props = {
    clientId: IClientUID
}

type State = {
    settings?: SettingsData
    error?: string
}

class ConfigurationTab extends Component<Props, State> {
    state: State = {
        settings: {
            current_setting: 1,
            settings: Array.from({ length: 10 }).fill({
                name: '',
                cylinder1_worktime_ms: 0,
                delay_after_cylinder1_ms: 0,
                motor_worktime_ms: 0,
                delay_after_motor_ms: 0,
                cylinder2_worktime_ms: 0,
                cylinder2_inner_lowtime_ms: 0,
                cylinder2_inner_hightime_ms: 0,
                cylinder2_inner_repeat: 0,
                delay_after_cylinder2_ms: 0,
                cylinder2_repeat: 0,
                cylinder3_worktime_ms: 0,
                delay_after_cylinder3_ms: 0,
            }) as SettingsData['settings'],
        },
    }

    async componentDidMount() {
        try {
            const settings = await GetFilterFillerSettings(this.props.clientId)
            this.setState({ settings })
        } catch (error) {
            this.setState({ error: (error as Error).message })
        }
    }

    handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        try {
            await SetFilterFillerSettings(this.props.clientId, this.state.settings!)
        } catch (error) {
            this.setState({ error: (error as Error).message })
            return
        }

        this.setState({ error: undefined })
        toast.success('Settings saved successfully!')
    }

    handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { name, value } = event.target
        const { settings } = this.state

        this.setState({
            settings: {
                ...settings,
                settings: [
                    ...settings!.settings.slice(0, index),
                    { ...settings!.settings[index], [name]: parseInt(value, 10) },
                    ...settings!.settings.slice(index + 1),
                ],
            } as SettingsData,
        })
    }

    handleStringChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { name, value } = event.target
        const { settings } = this.state

        this.setState({
            settings: {
                ...settings,
                settings: [
                    ...settings!.settings.slice(0, index),
                    { ...settings!.settings[index], [name]: value },
                    ...settings!.settings.slice(index + 1),
                ],
            } as SettingsData,
        })
    }

    handleCurrentSettingChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = event.target
        const { settings } = this.state

        this.setState({
            settings: {
                ...settings,
                current_setting: parseInt(value, 10),
            } as SettingsData,
        })
    }

    render() {
        const { settings, error } = this.state

        return (
            <form dir="ltr" onSubmit={this.handleSubmit} className="p-4 bg-white dark:bg-gray-800">
                {error && <ErrorAlert message={error} />}

                {this.renderActiveSettings(settings!)}

                {settings!.settings.map((setting, index) => (
                    <div
                        key={index}
                        className={classNames(
                            'bg-white dark:bg-gray-700 rounded-lg p-6 mt-6',
                            settings!.current_setting !== index + 1 && 'hidden',
                        )}
                    >
                        <h3 className="text-xl font-bold mb-4 text-black dark:text-white">Setting {index + 1}</h3>
                        {this.renderStringInput(index, 'Name', 'name', setting.name)}
                        {this.renderNumberInput(
                            index,
                            'Cylinder 1 work time (ms)',
                            'cylinder1_worktime_ms',
                            setting.cylinder1_worktime_ms,
                        )}
                        {this.renderNumberInput(
                            index,
                            'Delay after cylinder 1 (ms)',
                            'delay_after_cylinder1_ms',
                            setting.delay_after_cylinder1_ms,
                        )}
                        {this.renderNumberInput(
                            index,
                            'Motor work time (ms)',
                            'motor_worktime_ms',
                            setting.motor_worktime_ms,
                        )}
                        {this.renderNumberInput(
                            index,
                            'Delay after motor (ms)',
                            'delay_after_motor_ms',
                            setting.delay_after_motor_ms,
                        )}
                        {this.renderNumberInput(
                            index,
                            'Cylinder 2 work time (ms)',
                            'cylinder2_worktime_ms',
                            setting.cylinder2_worktime_ms,
                        )}
                        {this.renderNumberInput(
                            index,
                            'Cylinder 2 inner low time (ms)',
                            'cylinder2_inner_lowtime_ms',
                            setting.cylinder2_inner_lowtime_ms,
                        )}
                        {this.renderNumberInput(
                            index,
                            'Cylinder 2 inner high time (ms)',
                            'cylinder2_inner_hightime_ms',
                            setting.cylinder2_inner_hightime_ms,
                        )}
                        {this.renderNumberInput(
                            index,
                            'Cylinder 2 inner repeat',
                            'cylinder2_inner_repeat',
                            setting.cylinder2_inner_repeat,
                        )}
                        {this.renderNumberInput(
                            index,
                            'Delay after cylinder 2 (ms)',
                            'delay_after_cylinder2_ms',
                            setting.delay_after_cylinder2_ms,
                        )}
                        {this.renderNumberInput(
                            index,
                            'Cylinder 2 repeat',
                            'cylinder2_repeat',
                            setting.cylinder2_repeat,
                        )}
                        {this.renderNumberInput(
                            index,
                            'Cylinder 3 high time (ms)',
                            'cylinder3_hightime_ms',
                            setting.cylinder3_hightime_ms,
                        )}
                        {this.renderNumberInput(
                            index,
                            'Cylinder 3 low time (ms)',
                            'cylinder3_lowtime_ms',
                            setting.cylinder3_lowtime_ms,
                        )}
                        {this.renderNumberInput(
                            index,
                            'Delay after cylinder 3 (ms)',
                            'delay_after_cylinder3_ms',
                            setting.delay_after_cylinder3_ms,
                        )}
                    </div>
                ))}
                <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full mt-6 w-full text-center md:w-auto md:text-left"
                >
                    Save
                </button>
            </form>
        )
    }

    renderActiveSettings = (settings: SettingsData) => {
        return (
            <label className="block">
                <span className="text-gray-700 dark:text-gray-300">Active setting:</span>
                <div className="relative">
                    <select
                        className="block appearance-none w-full bg-white dark:bg-gray-700 border border-gray-400 dark:border-gray-600 hover:border-gray-500 dark:hover:border-gray-500 px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline text-black dark:text-white"
                        name="current_setting"
                        value={settings.current_setting}
                        onChange={this.handleCurrentSettingChange}
                    >
                        {settings.settings.map((setting, index) => (
                            <option key={index} value={index + 1}>
                                (Setting {index + 1}) {setting.name}
                            </option>
                        ))}
                    </select>
                </div>
            </label>
        )
    }

    renderNumberInput = (index: number, label: string, name: string, value: number) => {
        return (
            <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300">{label}:</label>
                <input
                    className="w-full px-3 py-2 placeholder-gray-300 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-500 focus:border-indigo-500 dark:focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-black dark:text-white"
                    type="number"
                    name={name}
                    value={value || 0}
                    onChange={(event) => this.handleNumberChange(event, index)}
                />
            </div>
        )
    }

    renderStringInput = (index: number, label: string, name: string, value: string) => {
        return (
            <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300">{label}:</label>
                <input
                    className="w-full px-3 py-2 placeholder-gray-300 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring focus:ring-indigo-200 dark:focus:ring-indigo-500 focus:border-indigo-500 dark:focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-700 text-black dark:text-white"
                    type="text"
                    name={name}
                    value={value || ''}
                    onChange={(event) => this.handleStringChange(event, index)}
                />
            </div>
        )
    }
}

export default ConfigurationTab